import { useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from '@/hooks/useToast';
import { UUID } from '@/types/common';

export const useCreateWorkOrderOperations = () => {
  const { toast } = useToast();

  /**
   * Create operations for a work order based on routing
   * @param selectedComponentIds - Optional array of component IDs to filter operations
   */
  const createWorkOrderOperations = useCallback(async (
    workOrderId: UUID,
    routingId: UUID,
    selectedComponentIds?: UUID[]
  ) => {
    try {
      console.log('Creating work order operations for Work Order:', workOrderId, 'with Routing:', routingId);
      console.log('Selected component IDs:', selectedComponentIds);

      // Fetch routing operations
      let query = supabase
        .from('routing_operations')
        .select('*')
        .eq('routing_id', routingId);

      // Filter by selected components if provided
      if (selectedComponentIds && selectedComponentIds.length > 0) {
        query = query.in('component_id', selectedComponentIds);
      }

      const { data: routingOperations, error: fetchError } = await query
        .order('sequence_no', { ascending: true });
      
      if (fetchError) {
        console.error('Error fetching routing operations:', fetchError);
        throw fetchError;
      }

      console.log('Found routing operations to copy:', routingOperations?.length || 0);
      console.log('Routing operations data:', routingOperations);
      
      if (!routingOperations || routingOperations.length === 0) {
        console.warn('No routing operations found for routing:', routingId);
        return [];
      }
      
      // Create work order operations based on routing operations
      // Assign sequential sequence numbers (1, 2, 3, ...) for the work order
      // The routing operations are already sorted by sequence_no from the query
      const workOrderOperations = routingOperations.map((routingOp, index) => ({
        work_order_id: workOrderId,
        operation_id: routingOp.operation_id,
        routing_operation_id: routingOp.id,
        sequence_no: index + 1, // Sequential numbering starting from 1
        workstation_id: routingOp.workstation_id,
        process_time: routingOp.process_time,
        setup_time: routingOp.setup_time,
        queue_time: routingOp.queue_time,
        is_subcontracted: routingOp.is_subcontracted,
        subcontractor_id: routingOp.subcontractor_id,
        qc_required: routingOp.qc_required,
        status: 'Not Started',
        actual_start_time: null,
        actual_end_time: null
      }));
      
      console.log('Work order operations to insert:', workOrderOperations);
      
      // Test the Supabase client connection
      console.log('Testing Supabase client connection...');
      const { data: testResult, error: testError } = await supabase
        .from('work_order_operations')
        .select('id')
        .limit(1);
      
      if (testError) {
        console.error('Supabase client connection test failed:', testError);
      } else {
        console.log('Supabase client connection test successful. Found', testResult?.length || 0, 'records');
      }
      
      // Insert work order operations
      const { data, error } = await supabase
        .from('work_order_operations')
        .insert(workOrderOperations)
        .select();
      
      if (error) {
        console.error('Error creating work order operations:', error);
        console.error('Error details:', error.message, error.details, error.hint);
        throw error;
      }
      
      console.log('Successfully created work order operations:', data?.length || 0);
      console.log('Inserted data:', data);
      
      return data;
    } catch (error: any) {
      console.error('Error in createWorkOrderOperations:', error);
      toast({
        title: 'Error creating work order operations',
        description: error.message,
        variant: 'destructive',
      });
      return [];
    }
  }, [toast]);

  return { createWorkOrderOperations };
};
